import { lazy, Suspense, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import type { Routes } from '../types';
import { Context as AuthContext } from '../api/auth/context';
import { Context as RolesContext } from '../api/roles/context';
import { Context as TravelFilesContext } from '../api/travelFiles/context';
import LeftSidebar from '../components/layout-blueprints/LeftSidebar';
import BreadcrumbsByLocation from '../components/BreadcrumbsByLocation/BreadcrumbsByLocation';
import { PermissionGuard } from '../components/Router/PermissionGuard';
import usePrefetchStaticData from '../hooks/usePrefetchStaticData';
import LogoAnimation from '../components/LogoAnimation/LogoAnimation';
import appSettingsApi from '../api/appSettings/api';
import MaintenancePage from '../components/MaintenancePage/MaintenancePage';
import { ReportIssueProvider } from '../components/ReportIssue';
import { HelpProvider } from '../help/HelpContext';
import { ensureContextData } from '../utils/contextData';

const MaintenanceSystemBanner = lazy(() => import('../components/MaintenanceSystemBanner'));
const NotFound = lazy(() => import('./NotFound'));
const Home = lazy(() => import('./Home'));
const Login = lazy(() => import('./Login'));
const Reservations = lazy(() => import('./Reservations'));
const Administration = lazy(() => import('./Administration'));
const Financial = lazy(() => import('./Financial'));
const Reports = lazy(() => import('./Reports'));
const SelfOperationTours = lazy(() => import('./Products/SelfOperationTours'));
const TourManagement = lazy(() => import('./TourManagement'));
const MessagingPage = lazy(() => import('./Messaging/MessagingPage'));
const HelpCenter = lazy(() => import('./Help'));
const MyTickets = lazy(() => import('./MyTickets'));
const PaymentResult = lazy(() => import('../components/PaymentResult'));
const InnerPaymentResult = lazy(() => import('../components/InnerPaymentResult'));
const ResetPassword = lazy(() => import('./ResetPassword'));
const SetPasswordPage = lazy(() => import('./SetPassword/SetPasswordPage'));
const SetupAccountPage = lazy(() => import('./SetupAccount/SetupAccountPage'));

/**
 * Single shared layout for all private routes.
 * LeftSidebar (sidebar + header) mounts ONCE and never unmounts on route changes.
 */
const PrivateLayout = () => {
    const auth = useContext(AuthContext);
    const rolesContext = useContext(RolesContext);
    const travelFilesContext = useContext(TravelFilesContext);
    const location = useLocation();
    const isStaticDataReady = usePrefetchStaticData(auth.isLoggedIn);
    const [animationDone, setAnimationDone] = useState(false);
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const tfFetchStarted = useRef(false);

    const handleAnimationComplete = useCallback(() => {
        setAnimationDone(true);
    }, []);

    // Check maintenance mode
    useEffect(() => {
        appSettingsApi
            .getByKey('maintenance_mode')
            .then((setting) => {
                const isOn = setting?.value === true || setting?.value === 'true';
                setMaintenanceMode(isOn);
            })
            .catch(() => setMaintenanceMode(false));
    }, []);

    // Detect if we're on a travel file page that needs loading
    const refNumber = useMemo(() => {
        if (!location.pathname.includes('/travel-file')) return null;
        const params = new URLSearchParams(location.search);
        return params.get('refNumber') || params.get('ref-number');
    }, [location.pathname, location.search]);

    // Fetch travel file alongside static data so the logo stays until it's ready
    useEffect(() => {
        if (refNumber && !tfFetchStarted.current) {
            tfFetchStarted.current = true;
            travelFilesContext.getTravelFile({ refNumber }).catch(() => {});
        }
    }, [refNumber, travelFilesContext]);

    const travelFileReady =
        !refNumber ||
        (!!travelFilesContext.selected &&
            String(travelFilesContext.selected.refNumber) === String(refNumber));

    // The permission resolver (useUserGrants) finds the current user's role by
    // searching the shared roles list, so that list has to be loaded before any
    // `can()` answer is meaningful. It has to be the WHOLE list, though.
    //
    // This used to fetch just the one role — `rolesContext.get({ _id })` — and
    // RolesProvider's get() ends in `setData(res)`, so that single role REPLACED
    // all of them. Everything else reading the list then saw one role: the Users
    // grid resolves each row's role name through it, so every user whose role was
    // not the viewer's own showed a blank Role column. It looked intermittent
    // because it raced the static-data prefetch, which fetches the full list —
    // whichever landed last won, so a few reloads would appear to "fix" it.
    //
    // ensureContextData fetches the full list and only when it is not already
    // held, so this costs nothing when the prefetch got there first.
    useEffect(() => {
        if (auth?.currUser) ensureContextData(rolesContext);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.currUser]);

    if (!auth.isLoggedIn) return <Redirect to="/login" />;

    const isSystemAdmin =
        !!auth.currUser?.isSuperUser ||
        (auth.currUser as { role_type?: string })?.role_type === 'System Admin';

    // Maintenance mode: block non-admin users
    if (maintenanceMode && !isSystemAdmin) {
        return <MaintenancePage />;
    }

    // Show animation until data is ready AND animation has completed its cycle
    // On travel file pages, also wait for the travel file to load (only during initial load)
    const holdForTravelFile = !animationDone && !travelFileReady;
    if (!isStaticDataReady || !animationDone || holdForTravelFile) {
        return (
            <LogoAnimation
                isReady={isStaticDataReady && travelFileReady}
                onComplete={handleAnimationComplete}
            />
        );
    }

    return (
        // Wraps LeftSidebar rather than sitting inside it: the header lives within LeftSidebar and its
        // "Report an issue" button consumes this context, as does the user menu.
        <ReportIssueProvider>
            <HelpProvider>
                <LeftSidebar>
                    {maintenanceMode && isSystemAdmin && (
                        <MaintenanceSystemBanner onTurnOff={() => setMaintenanceMode(false)} />
                    )}
                    <BreadcrumbsByLocation />
                    <Suspense fallback={<LogoAnimation />}>
                        <Switch>
                            <Route exact path="/" component={Home} />
                            <Route path="/messaging" component={MessagingPage} />
                            {/* No permission gate — help is filtered per article by the reader's
                        own permissions, so there is nothing here to withhold wholesale. */}
                            <Route path="/help/:articleId" component={HelpCenter} />
                            <Route exact path="/help" component={HelpCenter} />
                            {/* No permission gate — any user may see what they themselves reported. */}
                            <Route path="/my-tickets" component={MyTickets} />
                            <Route path="/finance" component={Financial} />
                            <Route path="/reports" component={Reports} />
                            <Route exact path="/operations">
                                <Redirect to="/administration/operations" />
                            </Route>
                            <Route path="/operations/self-operation-tours">
                                <PermissionGuard
                                    permission="setup.escorted_tour:view"
                                    Component={SelfOperationTours}
                                />
                            </Route>
                            <Route path="/administration" component={Administration} />
                            <Route path="/reservations" component={Reservations} />
                            <Route component={NotFound} />
                        </Switch>
                    </Suspense>
                </LeftSidebar>
            </HelpProvider>
        </ReportIssueProvider>
    );
};

// Public/global routes come BEFORE the catch-all private layout
const ROUTES: Routes = [
    { path: '/login', component: Login, key: '/login', title: 'Login' },

    { path: '/tour-management', component: TourManagement, key: '/tour-management', title: 'TourManagement' },

    { path: '/payment-result', component: PaymentResult, key: '/payment-result', title: 'PaymentResult' },
    {
        path: '/inner-payment-result',
        component: InnerPaymentResult,
        key: '/inner-payment-result',
        title: 'PaymentResult'
    },
    {
        path: '/reset-password',
        component: ResetPassword,
        key: '/reset-password',
        title: 'ResetPassword'
    },
    {
        path: '/set-password',
        component: SetPasswordPage,
        key: '/set-password',
        title: 'SetPassword'
    },
    {
        path: '/setup-account',
        component: SetupAccountPage,
        key: '/setup-account',
        title: 'SetupAccount'
    },
    // Catch-all: single private layout with sidebar/header (must be last)
    { path: '/', component: PrivateLayout, key: '/private-layout', title: 'App' }
];

export default ROUTES;
